import { ApolloClient, InMemoryCache, createHttpLink, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID!,
      userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID!,
      identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID!,
    }
  }
});

// Create HTTP link
const httpLink = createHttpLink({
  uri: process.env.REACT_APP_API_URL,
});

// Get WebSocket URL from the HTTP URL
const getWsUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  // Convert https:// to wss:// or http:// to ws://
  return apiUrl
    .replace('https://', 'wss://')
    .replace('http://', 'ws://')
    .replace('/graphql', '/graphql');
};

// Create WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: getWsUrl(),
    connectionParams: async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        return {
          authorization: token ? `Bearer ${token}` : '',
        };
      } catch (error) {
        console.error('Error getting auth token for WebSocket:', error);
        return {};
      }
    },
    shouldRetry: () => true, // Always reconnect
    keepAlive: 10_000,
  })
);

// Create auth link for HTTP requests
const authLink = setContext(async (_, { headers }) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {
      headers,
    };
  }
});

// Combine HTTP and WebSocket links
// Use WebSocket for subscriptions, HTTP for queries and mutations
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  authLink.concat(httpLink)
);

// Create Apollo Client with subscription support
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
